import noop from 'lodash/noop';
import React, { PropsWithChildren, ReactElement, useCallback } from 'react';
import { Spacing } from '../../design-system/spacing';
import { useBreakpoints } from '../../hooks/useBreakpoints';
import { styled } from '../../utils/styled';
import { Box } from '../Box';
import { Button } from '../Button';
import { ClickAwayListener } from '../ClickAwayListener';
import { Divider } from '../Divider';
import { CloseIcon } from '../Icons/CloseIcon';
import { Stack } from '../Stack';
import { SwipeableDrawer } from '../SwipeableDrawer';
import {
  ContentWrapper,
  Puller,
  StyledButtonContent,
} from './RightSideDrawer.styles';

const StyledStack = styled(Stack)`
  padding: ${({ theme }) => theme.spacing(Spacing.Medium)};
  width: 100%;
  align-items: center;
  justify-content: space-between;
  flex-direction: row;
`;

const StyledContainerPuller = styled(Stack)`
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing(Spacing.Small)};
`;

interface RightSideDrawerProps {
  readonly header?: ReactElement;
  readonly isOpen?: boolean;
  readonly noJustifyContent?: boolean;
  readonly actionButtons?: ReactElement;
  readonly handleClose: () => void;
  readonly handleOpen?: () => void;
  readonly onAnimationEnd?: () => void;
  readonly fullHeight?: boolean;
}

const ignoredMuiClasses = [
  'MuiDialog-root',
  'MuiDialog-container',
  'MuiButtonBase-root',
];

export function RightSideDrawer({
  isOpen,
  children,
  actionButtons,
  handleClose,
  onAnimationEnd,
  header,
  handleOpen = noop,
  noJustifyContent = false,
  fullHeight = false,
}: PropsWithChildren<RightSideDrawerProps>) {
  const { isMdUp } = useBreakpoints();

  const onClickAway = useCallback(() => {
    if (isOpen) {
      const target = document.activeElement as HTMLElement;
      const targetClasses = Array.from(target.classList);
      // The idea of this check is to NOT close the drawer then there is a dialog open or the user clicked on a button
      // This previously caused the drawer to close undesirably while interacting with other elements of the UI.
      if (
        !ignoredMuiClasses.some((muiClass) => targetClasses.includes(muiClass))
      ) {
        handleClose();
      }
    }
  }, [handleClose, isOpen]);

  return (
    <ClickAwayListener mouseEvent="onMouseUp" onClickAway={onClickAway}>
      <div>
        <SwipeableDrawer
          anchor={isMdUp ? 'right' : 'bottom'}
          fullHeight={fullHeight}
          onAnimationEnd={onAnimationEnd}
          onClose={handleClose}
          onOpen={handleOpen}
          open={isOpen}
          variant={isMdUp ? 'persistent' : 'temporary'}
        >
          <StyledContainerPuller>
            {!isMdUp && <Puller />}
            {header ? (
              <StyledStack>
                <Box>{header}</Box>
                <Button
                  color="neutral"
                  onClick={handleClose}
                  startIcon={
                    <CloseIcon aria-label="Close Drawer" title="Close Drawer" />
                  }
                  variant="text"
                />
              </StyledStack>
            ) : null}
          </StyledContainerPuller>
          <Divider noMargin />
          <ContentWrapper>{children}</ContentWrapper>
          {actionButtons ? (
            <>
              <Divider noMargin />
              <StyledButtonContent noJustifyContent={noJustifyContent}>
                {actionButtons}
              </StyledButtonContent>
            </>
          ) : null}
        </SwipeableDrawer>
      </div>
    </ClickAwayListener>
  );
}
